import type { ChatAttachment } from '../core/types';

import { isMarkdownAttachment } from '../../lib/attachments';

/** Rendered ImageFrame geometry shared by ImageFrame, VideoAttachment, and Echo feed estimates. */
export const IMAGE_FRAME_LIMITS = {
  tiny: 48,
  groupMaxH: 200,
  scrollerH: 320,
  max: 640
} as const;

/** Matches MessageAttachments: one attachment is inline, two or more render in the horizontal scroller. */
export type AttachmentLayoutMode = 'none' | 'single' | 'scroller';

/** ImageFrame supports group mode for AgentChatView even though Echo feed multi-attachments use scroller. */
export type AttachmentFrameMode = 'single' | 'group' | 'scroller';

export type ImageFrameVariant =
  | 'single:tiny-square'
  | 'single:regular'
  | 'scroller:centered'
  | 'scroller:regular'
  | 'group:tiny'
  | 'group:panorama-bg'
  | 'group:normal'
  | 'group:short-bg';

export interface ImageFrameLayout {
  readonly variant: ImageFrameVariant;
  readonly frameClass: string;
  readonly frameStyle: Record<string, number | string>;
  readonly imgClass: string;
  readonly imgStyle: Record<string, number | string>;
  readonly finalImgW: number;
  readonly finalImgH: number;
  readonly finalFrameW: number;
  readonly finalFrameH: number;
}

export interface AttachmentGeometryInput {
  readonly fileName?: string | null;
  readonly height?: number | null;
  readonly id?: string | null;
  readonly kind?: ChatAttachment['kind'] | string;
  readonly metadata?: {
    readonly content_type?: string | null;
    readonly file_name?: string | null;
    readonly height?: number | null;
    readonly thumbsize?: string | null;
    readonly width?: number | null;
  } | null;
  readonly upload?: {
    readonly state?: string | null;
  } | null;
  readonly variants?: {
    readonly large?: AttachmentImageVariant | null;
    readonly medium?: AttachmentImageVariant | null;
    readonly mux?: AttachmentMuxVariant | null;
  } | null;
  readonly width?: number | null;
}

export interface AttachmentImageVariant {
  readonly height?: number | null;
  readonly public_url?: string | null;
  readonly url?: string | null;
  readonly width?: number | null;
}

export interface AttachmentMuxVariant {
  readonly aspect_ratio?: string | null;
  readonly height?: number | null;
  readonly playback_id?: string | null;
  readonly width?: number | null;
}

export const ATTACHMENT_TEXT_GAP_PX = 6;
export const ATTACHMENT_FILE_CARD_HEIGHT_PX = 141;
export const ATTACHMENT_SCROLLER_FILE_HEIGHT_PX = IMAGE_FRAME_LIMITS.scrollerH;
export const ATTACHMENT_AUDIO_HEIGHT_PX = 56;
export const ATTACHMENT_UPLOAD_PREVIEW_SINGLE_DESKTOP_PX = 256;
export const ATTACHMENT_UPLOAD_PREVIEW_SINGLE_MOBILE_PX = 192;
export const ATTACHMENT_UPLOAD_PREVIEW_GROUP_PX = 192;
export const ATTACHMENT_UPLOAD_PREVIEW_MOBILE_BREAKPOINT_PX = 768;

/** Fit (w,h) into box (maxW,maxH). If allowUpscale=false, never increase size. */
export function fitWithin(
  w: number,
  h: number,
  maxW: number,
  maxH: number,
  allowUpscale: boolean
): { readonly h: number; readonly scale: number; readonly w: number } {
  const s = Math.min(maxW / w, maxH / h);
  const scale = allowUpscale ? s : Math.min(1, s);
  return {
    w: Math.max(1, Math.round(w * scale)),
    h: Math.max(1, Math.round(h * scale)),
    scale
  };
}

export function safeGeometryDimension(value: unknown, fallback: number | null = 1): number | null {
  const parsed = Number(value);

  if (!Number.isFinite(parsed) || parsed <= 0) {
    return fallback;
  }

  return Math.round(parsed);
}

export function parseAttachmentThumbsize(
  thumbsize: unknown
): { readonly height: number; readonly width: number } | null {
  if (typeof thumbsize !== 'string') return null;

  const match = thumbsize.trim().match(/^(\d+)x(\d+)$/i);
  if (!match) return null;

  const width = safeGeometryDimension(match[1], null);
  const height = safeGeometryDimension(match[2], null);
  if (!width || !height) return null;

  return { width, height };
}

export function resolveIntrinsicImageDimensions(
  rawWidth: unknown,
  rawHeight: unknown,
  thumbsize?: unknown
): { readonly height: number; readonly width: number } {
  let width = safeGeometryDimension(rawWidth, null);
  let height = safeGeometryDimension(rawHeight, null);
  const thumbDimensions = parseAttachmentThumbsize(thumbsize);
  const thumbWidth = thumbDimensions?.width || null;
  const thumbHeight = thumbDimensions?.height || null;

  if (!width && thumbWidth) width = thumbWidth;
  if (!height && thumbHeight) height = thumbHeight;

  if (!width && height && thumbWidth && thumbHeight) {
    width = Math.round((height * thumbWidth) / thumbHeight);
  } else if (!height && width && thumbWidth && thumbHeight) {
    height = Math.round((width * thumbHeight) / thumbWidth);
  }

  if (!width && !height) {
    width = 640;
    height = 360;
  } else if (!width) {
    width = Math.round(((height ?? 360) * 16) / 9);
  } else if (!height) {
    height = Math.round((width * 9) / 16);
  }

  return {
    width: safeGeometryDimension(width, 640) ?? 640,
    height: safeGeometryDimension(height, 360) ?? 360
  };
}

/**
 * Resolves video display dimensions from the best available source: mux stream
 * data first, then poster/upload metadata. A missing mux variant means the video
 * is still processing (or the row is a stale pre-webhook cache read), not that
 * the size is unknown — the frame must be reservable before playback exists.
 */
export function resolveVideoIntrinsicDimensions(
  attachment: AttachmentGeometryInput
): { readonly height: number; readonly width: number } {
  const muxVariant = attachment.variants?.mux;
  if (hasMuxDimensionData(muxVariant)) {
    return deriveVideoIntrinsicDimensions(muxVariant);
  }
  return resolveAttachmentImageDimensions(attachment);
}

function hasMuxDimensionData(
  muxVariant: AttachmentMuxVariant | null | undefined
): muxVariant is AttachmentMuxVariant {
  if (!muxVariant) return false;
  if (safeGeometryDimension(muxVariant.width, null) || safeGeometryDimension(muxVariant.height, null)) {
    return true;
  }
  // A malformed ratio must not win this branch — it would force the generic
  // 640x360 fallback over real poster/upload dimensions.
  return parseMuxAspectRatio(muxVariant.aspect_ratio) !== null;
}

function parseMuxAspectRatio(
  value: string | null | undefined
): { readonly ratioW: number; readonly ratioH: number } | null {
  if (typeof value !== 'string') return null;

  const [rawW, rawH] = value.split(':');
  const ratioW = safeGeometryDimension(rawW, null);
  const ratioH = safeGeometryDimension(rawH, null);
  if (!ratioW || !ratioH) return null;
  return { ratioW, ratioH };
}

export function deriveVideoIntrinsicDimensions(
  muxVariant: AttachmentMuxVariant | null | undefined
): { readonly height: number; readonly width: number } {
  const rawWidth = safeGeometryDimension(muxVariant?.width, null);
  const rawHeight = safeGeometryDimension(muxVariant?.height, null);

  let width = rawWidth;
  let height = rawHeight;

  if (!width || !height) {
    const ratio = parseMuxAspectRatio(muxVariant?.aspect_ratio);
    if (ratio) {
      if (!width && height) {
        width = Math.round((height * ratio.ratioW) / ratio.ratioH);
      } else if (!height && width) {
        height = Math.round((width * ratio.ratioH) / ratio.ratioW);
      } else if (!width && !height) {
        width = ratio.ratioW * 100;
        height = ratio.ratioH * 100;
      }
    }
  }

  return {
    width: safeGeometryDimension(width, 640) ?? 640,
    height: safeGeometryDimension(height, 360) ?? 360
  };
}

export function computeImageFrameLayout(
  wRaw: number,
  hRaw: number,
  mode: AttachmentFrameMode
): ImageFrameLayout {
  const w0 = Math.max(1, Math.round(wRaw));
  const h0 = Math.max(1, Math.round(hRaw));
  const { tiny, groupMaxH, scrollerH, max } = IMAGE_FRAME_LIMITS;

  if (mode === 'single') {
    const tinyCmp = w0 <= tiny && h0 <= tiny;

    if (tinyCmp) {
      return {
        variant: 'single:tiny-square',
        frameClass: 'inline-flex items-center justify-center overflow-hidden bg-white/40',
        frameStyle: {
          width: tiny,
          maxWidth: '100%',
          aspectRatio: '1'
        },
        imgClass: 'max-w-full max-h-full block object-contain',
        imgStyle: {
          width: w0,
          height: h0
        },
        finalImgW: Math.min(w0, tiny),
        finalImgH: Math.min(h0, tiny),
        finalFrameW: tiny,
        finalFrameH: tiny
      };
    }

    const fit = fitWithin(w0, h0, max, max, false);
    const isSmall = fit.w < 100 && fit.h < 100;
    return {
      variant: 'single:regular',
      frameClass: 'inline-block',
      frameStyle: {
        width: isSmall ? fit.w : '100%',
        maxWidth: fit.w,
        aspectRatio: `${fit.w} / ${fit.h}`
      },
      imgClass: 'block',
      imgStyle: {
        width: '100%',
        height: 'auto'
      },
      finalImgW: fit.w,
      finalImgH: fit.h,
      finalFrameW: fit.w,
      finalFrameH: fit.h
    };
  }

  if (mode === 'scroller') {
    if (h0 < scrollerH) {
      return {
        variant: 'scroller:centered',
        frameClass: 'inline-flex items-center justify-center overflow-hidden',
        frameStyle: {
          width: scrollerH,
          height: scrollerH,
          aspectRatio: '1'
        },
        imgClass: 'block max-w-full max-h-full object-contain',
        imgStyle: {
          width: w0,
          height: h0
        },
        finalImgW: w0,
        finalImgH: h0,
        finalFrameW: scrollerH,
        finalFrameH: scrollerH
      };
    }

    const frameW = Math.max(1, Math.round((w0 * scrollerH) / h0));

    return {
      variant: 'scroller:regular',
      frameClass: 'inline-block overflow-hidden',
      frameStyle: {
        width: frameW,
        height: scrollerH,
        aspectRatio: `${frameW} / ${scrollerH}`
      },
      imgClass: 'block size-full object-cover',
      imgStyle: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      },
      finalImgW: frameW,
      finalImgH: scrollerH,
      finalFrameW: frameW,
      finalFrameH: scrollerH
    };
  }

  if (w0 <= groupMaxH && h0 <= groupMaxH) {
    return {
      variant: 'group:tiny',
      frameClass: 'inline-flex items-center justify-center overflow-hidden bg-white/40',
      frameStyle: {
        width: groupMaxH,
        maxWidth: '100%',
        aspectRatio: '1'
      },
      imgClass: 'max-w-full max-h-full block object-contain',
      imgStyle: {
        width: w0,
        height: h0
      },
      finalImgW: Math.min(w0, groupMaxH),
      finalImgH: Math.min(h0, groupMaxH),
      finalFrameW: groupMaxH,
      finalFrameH: groupMaxH
    };
  }

  const widthAt200 = h0 > groupMaxH ? Math.round((w0 * groupMaxH) / h0) : w0;

  if (widthAt200 > max) {
    const imgH = Math.round((h0 * max) / w0);
    return {
      variant: 'group:panorama-bg',
      frameClass: 'inline-flex items-center justify-center overflow-hidden bg-white/40',
      frameStyle: {
        maxWidth: max,
        width: '100%',
        aspectRatio: `${max} / ${groupMaxH}`
      },
      imgClass: 'block',
      imgStyle: {
        width: '100%',
        height: 'auto',
        aspectRatio: `${w0} / ${h0}`
      },
      finalImgW: max,
      finalImgH: imgH,
      finalFrameW: max,
      finalFrameH: groupMaxH
    };
  }

  const frameW = widthAt200;

  if (h0 >= groupMaxH) {
    return {
      variant: 'group:normal',
      frameClass: 'inline-block overflow-hidden',
      frameStyle: {
        width: frameW,
        maxWidth: '100%',
        aspectRatio: `${frameW} / ${groupMaxH}`
      },
      imgClass: 'block',
      imgStyle: {
        width: '100%',
        height: '100%',
        objectFit: 'cover'
      },
      finalImgW: frameW,
      finalImgH: groupMaxH,
      finalFrameW: frameW,
      finalFrameH: groupMaxH
    };
  }

  return {
    variant: 'group:short-bg',
    frameClass: 'inline-flex items-center justify-center overflow-hidden bg-white/40',
    frameStyle: {
      width: frameW,
      maxWidth: '100%',
      aspectRatio: `${frameW} / ${groupMaxH}`
    },
    imgClass: 'block object-contain max-w-full max-h-full',
    imgStyle: {
      width: w0,
      height: h0
    },
    finalImgW: w0,
    finalImgH: h0,
    finalFrameW: frameW,
    finalFrameH: groupMaxH
  };
}

export function classifyAttachmentLayoutMode(attachmentsCount: number): AttachmentLayoutMode {
  if (attachmentsCount <= 0) return 'none';
  if (attachmentsCount === 1) return 'single';
  return 'scroller';
}

export function estimateAttachmentsHeight(
  attachments: readonly AttachmentGeometryInput[],
  contentWidth: number
): number {
  assertFinitePositive(contentWidth, 'contentWidth');
  if (attachments.length === 0) return 0;

  const layoutMode = classifyAttachmentLayoutMode(attachments.length);
  if (layoutMode === 'none') return 0;
  if (layoutMode === 'single') return estimateAttachmentHeight(attachments[0], contentWidth, 'single');

  return Math.max(
    ...attachments.map((attachment) => estimateAttachmentHeight(attachment, contentWidth, 'scroller'))
  );
}

export function estimateAttachmentHeight(
  attachment: AttachmentGeometryInput,
  contentWidth: number,
  mode: AttachmentFrameMode
): number {
  assertFinitePositive(contentWidth, 'contentWidth');

  if (isMarkdownAttachment(attachment)) {
    return mode === 'scroller' ? ATTACHMENT_SCROLLER_FILE_HEIGHT_PX : ATTACHMENT_FILE_CARD_HEIGHT_PX;
  }

  if (isUploadPreviewAttachment(attachment)) {
    return estimateUploadPreviewHeight(attachment, contentWidth, mode);
  }

  const contentType = attachmentContentType(attachment);

  if (isImageAttachment(attachment, contentType)) {
    return estimateImageLikeHeight(attachment, contentWidth, mode);
  }

  if (contentType === 'application/pdf') {
    return hasMediumVariant(attachment)
      ? estimatePdfHeight(attachment, contentWidth, mode)
      : 0;
  }

  if (isVideoAttachment(attachment, contentType)) {
    return estimateVideoHeight(attachment, contentWidth, mode);
  }

  if (contentType?.startsWith('audio/')) {
    return mode === 'scroller' ? ATTACHMENT_SCROLLER_FILE_HEIGHT_PX : ATTACHMENT_AUDIO_HEIGHT_PX;
  }

  if (attachment.kind === 'image' || attachment.kind === 'video') {
    return estimateImageLikeHeight(attachment, contentWidth, mode);
  }

  return mode === 'scroller' ? ATTACHMENT_SCROLLER_FILE_HEIGHT_PX : ATTACHMENT_FILE_CARD_HEIGHT_PX;
}

export function estimateImageFrameLayoutHeight(
  layout: ImageFrameLayout,
  contentWidth: number
): number {
  assertFinitePositive(contentWidth, 'contentWidth');

  if (layout.variant.startsWith('scroller:')) return layout.finalFrameH;

  const frameWidth = Math.max(1, layout.finalFrameW);
  const frameHeight = Math.max(1, layout.finalFrameH);
  const renderedWidth = Math.min(contentWidth, frameWidth);
  return (renderedWidth * frameHeight) / frameWidth;
}

export function attachmentInputsFromRendererRow(
  item: { readonly attachments?: readonly ChatAttachment[]; readonly rendererRow?: unknown } | undefined
): readonly AttachmentGeometryInput[] {
  const rendererRow = item?.rendererRow;
  if (isRecord(rendererRow) && Array.isArray(rendererRow.attachments)) {
    return rendererRow.attachments.filter(isRecord) as AttachmentGeometryInput[];
  }

  return item?.attachments ?? [];
}

export function hasMessageBodyAfterAttachments(
  item: { readonly body?: string; readonly containsOnlyAttachments?: boolean; readonly rendererRow?: unknown } | undefined
): boolean {
  const rendererRow = item?.rendererRow;
  if (isRecord(rendererRow)) {
    const containsOnlyAttachments = rendererRow.contains_only_attachments;
    if (containsOnlyAttachments === true) return false;

    const text = rendererRow.text;
    return typeof text === 'string' && text.length > 0;
  }

  if (item?.containsOnlyAttachments === true) return false;
  return typeof item?.body === 'string' && item.body.length > 0;
}

function estimateUploadPreviewHeight(
  attachment: AttachmentGeometryInput,
  contentWidth: number,
  mode: AttachmentFrameMode
): number {
  const contentType = attachmentContentType(attachment);
  if (contentType?.startsWith('image/')) {
    return estimateImageLikeHeight(attachment, contentWidth, mode);
  }

  if (mode === 'scroller') return ATTACHMENT_SCROLLER_FILE_HEIGHT_PX;
  if (mode === 'group') return ATTACHMENT_UPLOAD_PREVIEW_GROUP_PX;
  return contentWidth < ATTACHMENT_UPLOAD_PREVIEW_MOBILE_BREAKPOINT_PX
    ? ATTACHMENT_UPLOAD_PREVIEW_SINGLE_MOBILE_PX
    : ATTACHMENT_UPLOAD_PREVIEW_SINGLE_DESKTOP_PX;
}

function estimateImageLikeHeight(
  attachment: AttachmentGeometryInput,
  contentWidth: number,
  mode: AttachmentFrameMode
): number {
  const dimensions = resolveAttachmentImageDimensions(attachment);
  const layout = computeImageFrameLayout(dimensions.width, dimensions.height, mode);
  return estimateImageFrameLayoutHeight(layout, contentWidth);
}

function estimatePdfHeight(
  attachment: AttachmentGeometryInput,
  contentWidth: number,
  mode: AttachmentFrameMode
): number {
  const medium = attachment.variants?.medium;
  const width = safeGeometryDimension(medium?.width, 1) ?? 1;
  const height = safeGeometryDimension(medium?.height, 1) ?? 1;
  const layout = computeImageFrameLayout(width, height, mode);
  return estimateImageFrameLayoutHeight(layout, contentWidth);
}

function estimateVideoHeight(
  attachment: AttachmentGeometryInput,
  contentWidth: number,
  mode: AttachmentFrameMode
): number {
  // Must stay in lockstep with VideoAttachment.jsx: both reserve the final frame
  // even before mux playback exists, so the player arriving later never resizes
  // the row (the pop-in class of bugs at the pinned bottom).
  const dimensions = resolveVideoIntrinsicDimensions(attachment);
  const layout = computeImageFrameLayout(dimensions.width, dimensions.height, mode);
  return estimateImageFrameLayoutHeight(layout, contentWidth);
}

function resolveAttachmentImageDimensions(
  attachment: AttachmentGeometryInput
): { readonly height: number; readonly width: number } {
  // Variant preference mirrors display-source selection (medium, then large —
  // pickDisplayMedia in project.ts and VideoAttachment's posterUrl), so the
  // reserved frame matches what actually renders.
  const mediumWidth = safeGeometryDimension(attachment.variants?.medium?.width, null);
  const mediumHeight = safeGeometryDimension(attachment.variants?.medium?.height, null);
  const largeWidth = safeGeometryDimension(attachment.variants?.large?.width, null);
  const largeHeight = safeGeometryDimension(attachment.variants?.large?.height, null);
  const metadataWidth = safeGeometryDimension(attachment.metadata?.width, null);
  const metadataHeight = safeGeometryDimension(attachment.metadata?.height, null);
  const width = mediumWidth || largeWidth || metadataWidth || safeGeometryDimension(attachment.width, null);
  const height = mediumHeight || largeHeight || metadataHeight || safeGeometryDimension(attachment.height, null);

  return resolveIntrinsicImageDimensions(width, height, attachment.metadata?.thumbsize);
}

function isUploadPreviewAttachment(attachment: AttachmentGeometryInput): boolean {
  const uploadState = attachment.upload?.state;
  return Boolean(uploadState) && uploadState !== 'completed';
}

function isImageAttachment(attachment: AttachmentGeometryInput, contentType: string | null): boolean {
  if (contentType?.startsWith('image/')) return hasMediumVariant(attachment);
  return attachment.kind === 'image';
}

function isVideoAttachment(attachment: AttachmentGeometryInput, contentType: string | null): boolean {
  if (contentType?.startsWith('video/')) return true;
  return attachment.kind === 'video';
}

function hasMediumVariant(attachment: AttachmentGeometryInput): boolean {
  return Boolean(attachment.variants?.medium);
}

function attachmentContentType(attachment: AttachmentGeometryInput): string | null {
  const contentType = attachment.metadata?.content_type;
  return typeof contentType === 'string' ? contentType : null;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function assertFinitePositive(value: number, name: string) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new Error(`${name} must be a finite positive number`);
  }
}
